import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface ThemeState {
  isDarkMode: boolean;
  hasUserPreference: boolean;
  toggleDarkMode: () => void;
  setDarkMode: (isDark: boolean) => void;
}

// Get system preference for dark mode
const getSystemPreference = (): boolean => {
  if (typeof window === 'undefined') return true;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      isDarkMode: true, // Default to dark, will be overridden on init
      hasUserPreference: false,
      toggleDarkMode: () =>
        set((state) => ({
          isDarkMode: !state.isDarkMode,
          hasUserPreference: true, // User has made a choice
        })),
      setDarkMode: (isDark) =>
        set({
          isDarkMode: isDark,
          hasUserPreference: true,
        }),
    }),
    {
      name: 'theme-storage',
      partialize: (state) => ({
        isDarkMode: state.isDarkMode,
        hasUserPreference: state.hasUserPreference,
      }),
    }
  )
);

// Initialize dark mode on app load
export const initializeDarkMode = () => {
  const state = useThemeStore.getState();

  // If user has no stored preference, use system preference
  if (!state.hasUserPreference) {
    const systemPrefersDark = getSystemPreference();
    useThemeStore.setState({ isDarkMode: systemPrefersDark });
    updateDarkModeClass(systemPrefersDark);
  } else {
    // Use stored preference
    updateDarkModeClass(state.isDarkMode);
  }

  // Listen for system preference changes (only affects users without manual preference)
  if (typeof window !== 'undefined') {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', (e) => {
      const currentState = useThemeStore.getState();
      if (!currentState.hasUserPreference) {
        useThemeStore.setState({ isDarkMode: e.matches });
        updateDarkModeClass(e.matches);
      }
    });
  }
};

// Helper to update the dark class on the document
export const updateDarkModeClass = (isDark: boolean) => {
  if (typeof window !== 'undefined') {
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }
};

// Subscribe to store changes and update DOM
useThemeStore.subscribe((state) => {
  updateDarkModeClass(state.isDarkMode);
});
